import { useGateways } from "core/gateways-context";
import {
  cacheUpdateItemArray,
  cacheUpsertSingleItemInArray
} from "core/gateways/ReactQueryCacheHelpers";
import { LocationDto } from "core/models/location";
import { mapValues } from "lodash";
import {
  AppointmentTypeDto,
  AppointmentTypeSortOrderDto
} from "modules/booking/models";
import {
  AppointmentTypeCategoryDto,
  AppointmentTypeCategoryMappingDto,
  AppointmentTypeCategoryRequestDto
} from "modules/booking/models/appointmentTypeCategory";
import {
  PracticeNoticeDto,
  PracticeNoticeRequest
} from "modules/booking/models/practiceNotice.model";
import { isAvailableOnline } from "modules/booking/pages/appointmentTypes/utils";
import { useCallback } from "react";

import { HttpError } from "@bps/http-client";
import {
  DefinedInitialDataOptions,
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult
} from "@tanstack/react-query";

import { handleError } from "../../../common/apiErrors";
import { useCoreStore, useFeedbackStore } from "../../stores";
import { ProviderCacheKeys } from "./ProviderHooks";

const CacheKeys = {
  LocationAppointmentTypes: "location-appointment-types",
  LocationExclusionPeriods: "location-exclusion-periods",
  LocationProviders: "location-providers",
  Locations: "locations",
  LocationNotices: "location-notices",
  LocationAppointmentTypeCategoryMapping: "location-categories"
};

export const DEFAULT_EMAIL_CONTENT =
  "<p>Please be prepared for your appointment 5-10 mins early to assist us with keeping our appointments on time.</p>";
function useCreateLocationsQuery<TResult>(
  options?: Partial<
    DefinedInitialDataOptions<LocationDto[], HttpError, TResult>
  >
) {
  const { locationApi } = useGateways();
  return useQuery({
    queryKey: [CacheKeys.Locations],
    queryFn: locationApi.getLocations,
    ...options
  });
}

export const useLocationsQuery = useCreateLocationsQuery<LocationDto[]>;

export const useLocationQuery = (locationQueryOptions?: {
  enabled?: boolean;
}): UseQueryResult<LocationDto | undefined, HttpError> => {
  const { locationId } = useCoreStore();
  const options: Partial<
    DefinedInitialDataOptions<LocationDto[], HttpError, LocationDto | undefined>
  > = {
    select: useCallback(
      (data: LocationDto[]) => data?.find(l => l.id === locationId),
      [locationId]
    ),
    enabled: locationQueryOptions?.enabled
  };

  return useCreateLocationsQuery<LocationDto | undefined>(options);
};

export const useLocationAppointmentTypesQuery = (): UseQueryResult<
  AppointmentTypeDto[]
> => {
  const { locationApi } = useGateways();
  const { locationId } = useCoreStore();
  return useQuery<AppointmentTypeDto[]>({
    queryKey: [CacheKeys.LocationAppointmentTypes, locationId],
    queryFn: !locationId
      ? skipToken
      : () => locationApi.getAppointmentTypesForLocation(locationId),
    enabled: !!locationId
  });
};

export const useUpdateLocationMutation = () => {
  const queryClient = useQueryClient();
  const { locationApi } = useGateways();

  return useMutation<LocationDto, unknown, { location: LocationDto }>({
    mutationFn: ({ location }) => {
      return locationApi.updatePatchLocationPatch(
        // convert undefined values into null to remove it in patch
        mapValues(location, a => {
          return typeof a === "undefined" ? null : a;
        }) as Patch<LocationDto>
      );
    },
    onSuccess: (location: LocationDto) => {
      cacheUpsertSingleItemInArray({
        queryClient,
        queryKey: [CacheKeys.Locations],
        item: location
      });
    }
  });
};

export const useUpdateLocationLogoMutation = () => {
  const { locationApi } = useGateways();
  return useMutation<string, unknown, { id: string; logo: File }>({
    mutationFn: ({ id, logo }) => locationApi.uploadLogo(id, logo)
  });
};

export const useUpdateAppointmentSortOrderMutation = () => {
  const queryClient = useQueryClient();
  const { locationApi } = useGateways();
  const { locationId } = useCoreStore();

  return useMutation<
    AppointmentTypeDto[],
    unknown,
    { reOrdered: AppointmentTypeSortOrderDto[] }
  >({
    mutationFn: ({ reOrdered }) =>
      locationApi.updateSortOrder(locationId!, reOrdered),
    onSuccess: (appointmentTypes: AppointmentTypeDto[]) => {
      const queryKey = [CacheKeys.LocationAppointmentTypes, locationId];
      cacheUpdateItemArray(queryClient, queryKey, () => appointmentTypes);

      queryClient.invalidateQueries({
        queryKey: [CacheKeys.LocationAppointmentTypeCategoryMapping, locationId]
      });
    }
  });
};
const isAnyApptTypesOnline = (
  updatedApptType: AppointmentTypeDto,
  cachedApptTypes?: AppointmentTypeDto[]
) => {
  if (isAvailableOnline(updatedApptType)) return true;
  if (!cachedApptTypes || cachedApptTypes.length === 0) return false;

  const onlineAttpTypes = cachedApptTypes.filter(
    cAT => cAT.id !== updatedApptType.id && isAvailableOnline(cAT)
  );
  return onlineAttpTypes.length > 0;
};

export const useUpdateAppointmentTypeMutation = (
  hideSuccessMessage?: boolean
) => {
  const queryClient = useQueryClient();
  const { locationApi } = useGateways();
  const feedback = useFeedbackStore();
  const { locationId } = useCoreStore();
  const { data: location } = useLocationQuery();
  const { data: locationApptTypes } = useLocationAppointmentTypesQuery();

  const mutateLocation = useUpdateLocationMutation();
  return useMutation<AppointmentTypeDto, HttpError, AppointmentTypeDto>({
    mutationFn: async payload => {
      const updatedApptType =
        await locationApi.updateAppointmentTypeForLocation(locationId!, {
          ...payload,
          emailContentEnabled: true,
          emailContent: payload.emailContent || DEFAULT_EMAIL_CONTENT
        });

      const isUpdateNeeded =
        !isAnyApptTypesOnline(updatedApptType, locationApptTypes) &&
        location &&
        location.hasCancelRestrictions;

      if (isUpdateNeeded) {
        await mutateLocation.mutateAsync({
          location: { ...location, hasCancelRestrictions: false }
        });
      }

      return updatedApptType;
    },
    onSuccess: async (appointmentType: AppointmentTypeDto) => {
      if (
        !appointmentType.isAvailableExistingPatients &&
        !appointmentType.isAvailableNewPatients
      ) {
        await queryClient.invalidateQueries({
          queryKey: [CacheKeys.LocationProviders]
        });
        await queryClient.invalidateQueries({
          queryKey: [ProviderCacheKeys.ProviderAppointmentTypes]
        });
      }

      cacheUpsertSingleItemInArray({
        queryClient,
        queryKey: [CacheKeys.LocationAppointmentTypes, locationId],
        item: appointmentType
      });

      // Refetch cache for provider appointment types table
      await queryClient.refetchQueries({
        queryKey: [ProviderCacheKeys.ProviderAppointmentTypes]
      });

      // Refetch patient config cache for default on/off state for existing and new patients.
      await queryClient.refetchQueries({
        queryKey: [ProviderCacheKeys.PatientConfig]
      });

      await queryClient.invalidateQueries({
        queryKey: [CacheKeys.LocationAppointmentTypeCategoryMapping, locationId]
      });

      if (!hideSuccessMessage) {
        feedback.success("Appointment type details have been updated.");
      }
    },
    onError: error => handleError(error, "Appointment type update")
  });
};

export const useLocationNoticesQuery = (): UseQueryResult<
  PracticeNoticeDto[]
> => {
  const { locationApi } = useGateways();
  const { locationId } = useCoreStore();

  return useQuery<PracticeNoticeDto[]>({
    queryKey: [CacheKeys.LocationNotices, locationId],
    queryFn: !locationId
      ? skipToken
      : () => locationApi.getNoticesForLocation(locationId),
    enabled: !!locationId
  });
};

export const useAddPracticeNoticeMutation = () => {
  const queryClient = useQueryClient();
  const { locationApi } = useGateways();
  return useMutation<PracticeNoticeDto, HttpError, PracticeNoticeRequest>({
    mutationFn: request => locationApi.addNotice(request),
    onSuccess: (updated: PracticeNoticeDto) => {
      const { locationId } = updated;

      cacheUpsertSingleItemInArray({
        queryClient,
        queryKey: [CacheKeys.LocationNotices, locationId],
        item: updated
      });
    }
  });
};

export const useUpdatePracticeNoticeMutation = () => {
  const queryClient = useQueryClient();
  const { locationApi } = useGateways();
  return useMutation<PracticeNoticeDto, HttpError, PracticeNoticeRequest>({
    mutationFn: request => {
      if (request.id) return locationApi.updateNotice(request.id, request);
      else {
        throw Error("Id is required to update the notice");
      }
    },
    onSuccess: (updated: PracticeNoticeDto) => {
      const { locationId } = updated;

      cacheUpsertSingleItemInArray({
        queryClient,
        queryKey: [CacheKeys.LocationNotices, locationId],
        item: updated
      });
    }
  });
};

export const useDeletePracticeNoticeMutation = () => {
  const queryClient = useQueryClient();
  const { locationApi } = useGateways();

  return useMutation<void, HttpError, { locationId: string; noticeId: string }>(
    {
      mutationFn: ({ locationId, noticeId }) =>
        locationApi.deleteNotice(locationId, noticeId),
      onSuccess: (_, { locationId, noticeId }) => {
        cacheUpdateItemArray(
          queryClient,
          [CacheKeys.LocationNotices, locationId],
          cached => cached.filter(prev => prev.id !== noticeId)
        );
      }
    }
  );
};

export const useLocationNoticeQuery = (
  noticeId?: string,
  refetch?: boolean | "always"
): UseQueryResult<PracticeNoticeDto | undefined> => {
  const { locationApi } = useGateways();
  const { locationId } = useCoreStore();
  return useQuery<PracticeNoticeDto | undefined>({
    queryKey: [locationId, noticeId],
    queryFn: !locationId
      ? skipToken
      : async () => {
          if (noticeId)
            return await locationApi.getNoticeForLocation(locationId, noticeId);
          return undefined;
        },
    enabled: !!noticeId && !!locationId,
    refetchOnMount: refetch
  });
};

export const useAddAppointmentTypeCategoryMutation = () => {
  const { locationApi } = useGateways();
  const { locationId } = useCoreStore();

  const queryClient = useQueryClient();
  return useMutation<
    AppointmentTypeCategoryDto,
    HttpError,
    AppointmentTypeCategoryRequestDto
  >({
    mutationFn: request =>
      locationApi.addAppointmentTypeCategory(locationId!, request),
    onSuccess: createdCategory => {
      queryClient.setQueryData(
        [CacheKeys.LocationAppointmentTypeCategoryMapping, locationId],
        (oldData: AppointmentTypeCategoryMappingDto) => {
          const newAppointmentTypes = oldData.appointmentTypes.filter(
            at =>
              !createdCategory.appointmentTypes.find(cat => at.id === cat.id)
          );

          const newCategories = [...oldData.categories, createdCategory];

          return {
            appointmentTypes: newAppointmentTypes,
            categories: newCategories
          };
        }
      );
    }
  });
};

export const useUpdateAppointmentTypeCategoryMutation = () => {
  const { locationApi } = useGateways();
  const { locationId } = useCoreStore();

  const queryClient = useQueryClient();
  return useMutation<
    AppointmentTypeCategoryDto,
    HttpError,
    {
      id: string;
      request: AppointmentTypeCategoryRequestDto;
      refreshCache?: boolean;
    }
  >({
    mutationFn: ({ id, request }) =>
      locationApi.updateAppointmentTypeCategory(locationId!, id, request),
    onSuccess: (updatedCategory, { refreshCache = true }) => {
      refreshCache &&
        queryClient.setQueryData(
          [CacheKeys.LocationAppointmentTypeCategoryMapping, locationId],
          (oldData: AppointmentTypeCategoryMappingDto) => {
            const oldCategory = oldData.categories.find(
              category =>
                category.appointmentTypeCategoryId ===
                updatedCategory.appointmentTypeCategoryId
            );

            // Any appt types removed from the category in the update
            const removedAppointmentTypes =
              oldCategory?.appointmentTypes.filter(
                a => !updatedCategory.appointmentTypes.find(b => b.id === a.id)
              ) ?? [];

            const uncategorisedAppointmentTypes = [
              // Filters out any appt types added to the category in the update
              ...oldData.appointmentTypes.filter(
                at =>
                  !updatedCategory.appointmentTypes.find(x => x.id === at.id)
              ),
              ...removedAppointmentTypes
            ];

            const newCategories = [
              ...oldData.categories.filter(
                category =>
                  category.appointmentTypeCategoryId !==
                  updatedCategory.appointmentTypeCategoryId
              ),
              updatedCategory
            ];

            const newData: AppointmentTypeCategoryMappingDto = {
              categories: newCategories,
              appointmentTypes: uncategorisedAppointmentTypes
            };

            return newData;
          }
        );
    }
  });
};

export const useAppointmentTypeCategoryMappingQuery = () => {
  const { locationApi } = useGateways();
  const { locationId } = useCoreStore();

  return useQuery<AppointmentTypeCategoryMappingDto, HttpError>({
    queryKey: [CacheKeys.LocationAppointmentTypeCategoryMapping, locationId],
    queryFn: !locationId
      ? skipToken
      : () => locationApi.getAppointmentTypeCategoryMapping(locationId),
    enabled: !!locationId
  });
};

export const useDeleteAppointmentTypeCategoryMutation = () => {
  const { locationApi } = useGateways();
  const { locationId } = useCoreStore();
  const queryClient = useQueryClient();

  return useMutation<void, HttpError, string>({
    mutationFn: id =>
      locationApi.deleteAppointmentTypeCategory(locationId!, id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [CacheKeys.LocationAppointmentTypeCategoryMapping, locationId]
      });

      // Appointment types in a deleted category have their sortOrder to null updated by the backend
      queryClient.invalidateQueries({
        queryKey: [CacheKeys.LocationAppointmentTypes, locationId]
      });
    }
  });
};
